import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Modal";
import type { BridgeBlock } from "../../lib/ingestReviewApi";

export type BlockChoice = "add_as_new_version" | "replace" | "allow_duplicate" | "auto_fix";

const CHOICES: Record<BlockChoice, { label: string; hint: string; destructive?: boolean }> = {
  add_as_new_version: { label: "Add as new draft version", hint: "Adds a draft version to the chapter. Students keep the live version until you publish." },
  replace: { label: "Replace the existing exam", hint: "Overwrites the unpublished exam. Its previous content is saved in the admin log.", destructive: true },
  allow_duplicate: { label: "Create anyway", hint: "Creates a new unpublished exam next to the similar one." },
  auto_fix: { label: "Auto-fix", hint: "Shortens and reorganises the slides listed above so they fit, without removing any text, then approves again." },
};
const ORDER: BlockChoice[] = ["add_as_new_version", "replace", "allow_duplicate", "auto_fix"];

type Props = {
  blocks: BridgeBlock[] | null;
  busy: boolean;
  onChoose: (choice: BlockChoice, itemIds: string[]) => void;
  onClose: () => void;
};

/** Shown when an approval was not applied. The items stay draft until the admin picks an option or keeps them as draft. */
export function IngestBlockedDialog({ blocks, busy, onChoose, onClose }: Props) {
  const list = blocks ?? [];
  const available = ORDER.filter((c) => list.some((b) => b.options.includes(c)));
  return (
    <Modal isOpen={!!blocks} onClose={onClose} title="Not approved yet" description="These items are still in draft. Nothing was changed for them." size="lg">
      <div className="space-y-4">
        <ul className="space-y-3">
          {list.map((b, i) => (
            <li key={i} className="space-y-1 text-sm">
              <p>
                <Badge variant="warning">{b.kind === "duplicate" ? "Possible duplicate" : b.kind === "refused" ? "Not created by ingestion" : b.kind === "invalid_notes" ? "Slides need fixing" : "Already exists"}</Badge>{" "}
                {b.reason} <span className="text-muted-foreground">({b.item_ids.length} item{b.item_ids.length === 1 ? "" : "s"})</span>
              </p>
              {b.errors && b.errors.length > 0 && (
                <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                  {b.errors.map((e, k) => (
                    <li key={k}>
                      {e.message}
                      {e.slide_id && <span className="text-xs"> ({e.slide_id})</span>}
                    </li>
                  ))}
                </ul>
              )}
              {b.possible_duplicates && b.possible_duplicates.length > 0 && (
                <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
                  {b.possible_duplicates.map((d) => (
                    <li key={d.id}>
                      {d.title} · {d.is_published ? "published" : "draft"} · {d.ingested ? "ingested" : "added by hand"}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
        <div className="space-y-3">
          {available.map((c) => (
            <div key={c} className="space-y-1">
              <Button
                size="sm"
                variant={CHOICES[c].destructive ? "destructive" : "primary"}
                loading={busy}
                onClick={() => onChoose(c, list.filter((b) => b.options.includes(c)).flatMap((b) => b.item_ids))}
              >
                {CHOICES[c].label}
              </Button>
              <p className="text-sm text-muted-foreground">{CHOICES[c].hint}</p>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={onClose}>
            Keep as draft
          </Button>
        </div>
      </div>
    </Modal>
  );
}
